<template>
  <div class="apps-page">
    <div class="apps-toolbar">
      <button type="button" class="toolbar-btn toolbar-btn-primary" @click="showCreateApp">
        <font-awesome-icon icon="plus" class="toolbar-btn-icon" />new app
      </button>
      <div class="toolbar-menu" ref="templateMenu">
        <button type="button" class="toolbar-btn" :aria-expanded="templateMenuOpen ? 'true' : 'false'" @click="templateMenuOpen = !templateMenuOpen">
          <font-awesome-icon icon="box" class="toolbar-btn-icon" />templates
        </button>
        <div v-if="templateMenuOpen" class="toolbar-menu-panel">
          <button type="button" class="toolbar-menu-item" @click="showImportTemplate">
            <font-awesome-icon icon="file-import" class="toolbar-btn-icon" />import template
          </button>
          <button type="button" class="toolbar-menu-item" v-if="canCreateTemplateApps && uniqueTitle" @click="showSaveTemplate">
            <font-awesome-icon icon="save" class="toolbar-btn-icon" />save as template
          </button>
        </div>
      </div>
    </div>
    <resource-notice class="apps-notice" />
    <div v-if="templateNotice" class="template-notice">
      <span>{{ templateNotice }}</span>
      <button type="button" class="copy-dismiss" @click="templateNotice = ``">dismiss</button>
    </div>
    <div v-if="copyInProgress || copyBannerError" class="copy-progress-banner">
      <div class="copy-progress-label">
        <span>{{ copyBannerError || copyMessage || 'copying app' }}</span>
        <span v-if="copyInProgress" class="copy-progress-pct">{{ copyProgress }}%</span>
        <button v-if="copyBannerError" type="button" class="copy-dismiss" @click="copyBannerError = null">dismiss</button>
      </div>
      <div v-if="copyInProgress" class="copy-progress-track">
        <div class="copy-progress-fill" :style="{ width: copyProgress + '%' }"></div>
      </div>
    </div>
    <div class="apps-layout">
      <div class="apps-sidebar">
        <div class="sidebar-header">APPS</div>
        <p v-if="!apps.length" class="sidebar-empty">No apps yet</p>
        <div v-else class="app-list">
          <button
            v-for="app in apps"
            :key="app.uniqueTitle"
            type="button"
            class="app-item"
            :class="{ 'app-item-active': uniqueTitle === app.uniqueTitle }"
            @click="loadApp(app)"
          >
            <font-awesome-icon icon="th-large" class="app-item-icon" />
            <span class="app-item-text">
              <span class="app-item-title">{{ (app && app.title) ? app.title : 'invalid app title' }}</span>
              <span v-if="app && app.uniqueTitle && app.uniqueTitle !== app.title" class="app-item-id">{{ app.uniqueTitle }}</span>
            </span>
          </button>
        </div>
      </div>
      <div class="apps-detail">
        <div v-if="app" class="detail-content">
          <div class="detail-header">
            <h2 class="detail-title">{{ title }}</h2>
            <p class="detail-id">unique title <span>{{ uniqueTitle }}</span></p>
          </div>
          <p v-if="app.description" class="detail-description">{{ app.description }}</p>
          <div class="detail-actions">
            <a :href="designerUrl()" class="detail-action-btn detail-action-primary">
              <font-awesome-icon icon="drafting-compass" class="detail-action-icon" />design app
            </a>
            <button type="button" class="detail-action-btn" @click="showEditApp">
              <font-awesome-icon icon="pen" class="detail-action-icon" />edit
            </button>
            <button type="button" class="detail-action-btn" @click="showCopyApp">
              <font-awesome-icon icon="copy" class="detail-action-icon" />copy
            </button>
            <button type="button" class="detail-action-btn detail-action-danger" @click="showDeleteApp">
              <font-awesome-icon icon="trash" class="detail-action-icon" />delete
            </button>
          </div>
          <p v-if="statsError" class="detail-stats-error">{{ statsError }}</p>
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-value">{{ stats.spreadsheets }}</div>
              <div class="stat-label"><font-awesome-icon icon="table" class="stat-icon" />spreadsheets</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{{ stats.records }}</div>
              <div class="stat-label"><font-awesome-icon icon="database" class="stat-icon" />records</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{{ stats.pages }}</div>
              <div class="stat-label"><font-awesome-icon icon="file-alt" class="stat-icon" />pages</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{{ stats.views }}</div>
              <div class="stat-label"><font-awesome-icon icon="eye" class="stat-icon" />views</div>
            </div>
          </div>
          <div v-if="stats.lastUpdated" class="detail-last-updated">
            <font-awesome-icon icon="clock" class="last-updated-icon" />last updated {{ formatDate(stats.lastUpdated) }}
          </div>
        </div>
        <div v-else class="detail-empty-state">
          <font-awesome-icon icon="th-large" class="detail-empty-icon" />
          <p>{{ apps.length ? 'Select an app' : 'No apps yet' }}</p>
          <button v-if="!apps.length" type="button" class="detail-action-btn detail-action-primary" @click="showCreateApp">
            <font-awesome-icon icon="plus" class="detail-action-icon" />new app
          </button>
        </div>
      </div>
    </div>

    <b-modal v-model="createAppModalVisible" title="create new app" content-class="app-modal" @ok="createApp">
      <template #title>
        create new app
        <modal-help topic="create-app" />
      </template>
      <div class="modal-form">
        <div class="modal-field">
          <label class="modal-label" for="new-app-title">app title</label>
          <input id="new-app-title" v-model="newAppTitle" placeholder="my new app name" required class="modal-input" :class="{ 'modal-input-invalid': createTitleInvalid }" />
          <p v-if="createTitleInvalid" class="modal-error">an app title is required</p>
        </div>
        <div class="modal-field">
          <label class="modal-label" for="new-app-id">unique title</label>
          <input id="new-app-id" :value="newAppUniqueTitle" @input="onNewUniqueTitleInput" placeholder="my_new_app_name" required class="modal-input" :class="{ 'modal-input-invalid': createUniqueTitleInvalid }" />
          <p v-if="createUniqueTitleEmpty" class="modal-error">a unique title is required</p>
          <p v-else-if="createUniqueTitleMalformed" class="modal-error">use letters, numbers, and underscores, and start with a letter or underscore</p>
          <p v-else-if="createUniqueTitleTaken" class="modal-error">this unique title is already used by another app</p>
          <p v-else class="modal-hint">Used in addresses. Letters, numbers, and underscores.</p>
        </div>
        <p v-if="createError" class="modal-error">{{ createError }}</p>
      </div>
    </b-modal>

    <b-modal v-model="deleteAppModalVisible" title="delete app" content-class="app-modal" @ok="deleteApp">
      <template #title>
        delete app
        <modal-help topic="delete-app" />
      </template>
      <div class="modal-form">
        <p class="modal-warning">Delete app <strong>{{ title }}</strong>? This cannot be undone.</p>
        <div class="modal-field">
          <label class="modal-label" for="delete-app-title">enter the app title to confirm deletion</label>
          <input id="delete-app-title" v-model="deleteAppTitle" placeholder="my app name" required class="modal-input" :class="{ 'modal-input-invalid': deleteMismatch }" />
          <p v-if="deleteMismatch" class="modal-error">type the app title exactly</p>
        </div>
        <p v-if="deleteError" class="modal-error">{{ deleteError }}</p>
      </div>
    </b-modal>

    <b-modal
      v-model="copyAppModalVisible"
      title="copy app"
      content-class="app-modal"
      :ok-title="copyInProgress ? 'copying…' : 'copy'"
      :ok-disabled="copyInProgress"
      :cancel-disabled="copyInProgress"
      :no-close-on-backdrop="copyInProgress"
      :no-close-on-esc="copyInProgress"
      @ok="startCopy"
    >
      <template #title>
        copy app
        <modal-help topic="copy-app" />
      </template>
      <div v-if="copyInProgress" class="copy-progress">
        <div class="copy-progress-label">
          <span>{{ copyMessage || 'copying app' }}</span>
          <span class="copy-progress-pct">{{ copyProgress }}%</span>
        </div>
        <div class="copy-progress-track">
          <div class="copy-progress-fill" :style="{ width: copyProgress + '%' }"></div>
        </div>
      </div>
      <div v-else class="modal-form">
        <div class="modal-field">
          <label class="modal-label">app title</label>
          <input v-model="copyAppTitle" placeholder="copy of my app" required class="modal-input" :class="{ 'modal-input-invalid': copyTitleInvalid }" />
          <p v-if="copyTitleInvalid" class="modal-error">an app title is required</p>
        </div>
        <div class="modal-field">
          <label class="modal-label">unique title</label>
          <input :value="copyAppUniqueTitle" @input="onCopyUniqueTitleInput" placeholder="copy_of_my_app" required class="modal-input" :class="{ 'modal-input-invalid': copyUniqueTitleInvalid }" />
          <p v-if="copyUniqueTitleEmpty" class="modal-error">a unique title is required</p>
          <p v-else-if="copyUniqueTitleMalformed" class="modal-error">use letters, numbers, and underscores</p>
          <p v-else-if="copyUniqueTitleTaken" class="modal-error">this unique title is already used by another app</p>
        </div>
        <label class="modal-check">
          <input type="checkbox" v-model="copyRecords" />
          <span>copy records</span>
        </label>
        <p class="modal-hint">The app structure is always copied. Check this to copy every record as well.</p>
        <p v-if="copyError" class="modal-error">{{ copyError }}</p>
      </div>
    </b-modal>

    <b-modal
      v-model="saveTemplateModalVisible"
      title="save as template"
      content-class="app-modal"
      :ok-title="templateBusy ? 'saving…' : 'save'"
      :ok-disabled="templateBusy"
      @ok="saveTemplate"
    >
      <template #title>
        save as template
        <modal-help topic="save-template" />
      </template>
      <div class="modal-form">
        <div class="modal-field">
          <label class="modal-label">template title</label>
          <input v-model="saveTemplateTitle" placeholder="CRM" required class="modal-input" :class="{ 'modal-input-invalid': saveTemplateTitleInvalid }" />
          <p v-if="saveTemplateTitleInvalid" class="modal-error">a template title is required</p>
        </div>
        <div class="modal-field">
          <label class="modal-label">template id</label>
          <input v-model="saveTemplateId" placeholder="crm" required class="modal-input" :class="{ 'modal-input-invalid': saveTemplateIdInvalid }" />
          <p v-if="saveTemplateIdInvalid" class="modal-error">use letters, numbers, and underscores</p>
        </div>
        <div class="modal-field">
          <label class="modal-label">description</label>
          <textarea v-model="saveTemplateDescription" placeholder="What this template is for" class="modal-input modal-textarea" rows="3"></textarea>
        </div>
        <label class="modal-check">
          <input type="checkbox" v-model="saveIncludeRecords" />
          <span>include records</span>
        </label>
        <p class="modal-hint">The app structure is always saved. Saving an id you already published replaces that template.</p>
        <p v-if="templateError" class="modal-error">{{ templateError }}</p>
      </div>
    </b-modal>

    <b-modal
      v-model="importTemplateModalVisible"
      title="import template"
      content-class="app-modal"
      :ok-title="templateBusy ? 'importing…' : 'import'"
      :ok-disabled="templateBusy || !selectedTemplate"
      @ok="importTemplate"
    >
      <template #title>
        import template
        <modal-help topic="import-template" />
      </template>
      <div class="modal-form">
        <p v-if="templatesLoading" class="modal-hint">loading templates…</p>
        <p v-else-if="!templates.length" class="modal-hint">No template apps are available yet.</p>
        <div v-else class="template-list">
          <button
            v-for="template in templates"
            :key="template.id"
            type="button"
            class="template-choice"
            :class="{ 'template-choice-active': selectedTemplateId === template.id }"
            @click="selectTemplate(template)"
          >
            <span class="template-choice-title">{{ template.title }}</span>
            <span class="template-choice-meta">{{ template.description || template.id }}</span>
            <span v-if="template.includeRecords" class="template-choice-meta">includes records</span>
          </button>
        </div>
        <div v-if="selectedTemplate" class="modal-field">
          <label class="modal-label">app title</label>
          <input v-model="importAppTitle" placeholder="my app" required class="modal-input" :class="{ 'modal-input-invalid': importTitleInvalid }" />
          <p v-if="importTitleInvalid" class="modal-error">an app title is required</p>
        </div>
        <div v-if="selectedTemplate" class="modal-field">
          <label class="modal-label">unique title</label>
          <input :value="importAppUniqueTitle" @input="onImportUniqueTitleInput" placeholder="my_app" required class="modal-input" :class="{ 'modal-input-invalid': importUniqueTitleInvalid }" />
          <p v-if="importUniqueTitleEmpty" class="modal-error">a unique title is required</p>
          <p v-else-if="importUniqueTitleMalformed" class="modal-error">use letters, numbers, and underscores</p>
          <p v-else-if="importUniqueTitleTaken" class="modal-error">this unique title is already used by another app</p>
        </div>
        <label v-if="selectedTemplate && selectedTemplate.includeRecords" class="modal-check">
          <input type="checkbox" v-model="importRecords" />
          <span>import records</span>
        </label>
        <button
          v-if="selectedTemplate && selectedTemplate.owned"
          type="button"
          class="template-delete"
          :disabled="templateBusy"
          @click="deleteSelectedTemplate"
        >delete this template</button>
        <p v-if="templateError" class="modal-error">{{ templateError }}</p>
      </div>
    </b-modal>

    <b-modal v-model="editAppModalVisible" title="edit app" content-class="app-modal" @ok="saveApp">
      <template #title>
        edit app
        <modal-help topic="edit-app" />
      </template>
      <div class="modal-form">
        <div class="modal-field">
          <label class="modal-label">app title</label>
          <input v-model="editTitle" placeholder="App title" required class="modal-input" />
        </div>
        <div class="modal-field">
          <label class="modal-label">description</label>
          <textarea v-model="editDescription" placeholder="Add a description for this app..." class="modal-input modal-textarea" rows="4"></textarea>
        </div>
        <p v-if="editError" class="modal-error">{{ editError }}</p>
      </div>
    </b-modal>
  </div>
</template>

<script>

  import axios from 'axios'
  import _ from 'lodash'
  import { designerUrl as accountDesignerUrl } from '../../../shared/designer-host.js'
  import ResourceNotice from './resource-notice.vue'

  export default {
    data () {

      return {
        uniqueTitle: ``,
        title: ``,
        rowHeaders: [],
        app: null,
        apps: [],
        items: [],
        newAppTitle: ``,
        newAppUniqueTitle: ``,
        newAppUniqueTitleManuallyEdited: false,
        createSubmitAttempted: false,
        createError: ``,
        deleteAppTitle: ``,
        deleteMismatch: false,
        deleteError: ``,
        editTitle: ``,
        editDescription: ``,
        editError: ``,
        stats: { spreadsheets: 0, records: 0, pages: 0, views: 0, lastUpdated: null },
        statsError: ``,
        appOptions: [],
        createAppModalVisible: false,
        deleteAppModalVisible: false,
        editAppModalVisible: false,
        copyAppModalVisible: false,
        copyAppTitle: ``,
        copyAppUniqueTitle: ``,
        copyAppUniqueTitleManuallyEdited: false,
        copyRecords: false,
        copySubmitAttempted: false,
        copyInProgress: false,
        copyProgress: 0,
        copyMessage: ``,
        copyError: null,
        copyBannerError: null,
        copyTargetUniqueTitle: ``,
        copyPollTimer: null,
        canCreateTemplateApps: false,
        templateMenuOpen: false,
        templateNotice: ``,
        templateError: ``,
        templateBusy: false,
        templates: [],
        templatesLoading: false,
        selectedTemplateId: ``,
        saveTemplateModalVisible: false,
        importTemplateModalVisible: false,
        saveTemplateId: ``,
        saveTemplateTitle: ``,
        saveTemplateDescription: ``,
        saveIncludeRecords: false,
        saveSubmitAttempted: false,
        importAppTitle: ``,
        importAppUniqueTitle: ``,
        importUniqueTitleManuallyEdited: false,
        importRecords: true,
        importSubmitAttempted: false
      }
    },
    computed: {

      createTitleInvalid () {

        return this.createSubmitAttempted && !String(this.newAppTitle || ``).trim()
      },
      createUniqueTitleEmpty () {

        return this.createSubmitAttempted && !String(this.newAppUniqueTitle || ``).trim()
      },
      createUniqueTitleMalformed () {

        const uniqueTitle = String(this.newAppUniqueTitle || ``).trim()

        if (!uniqueTitle) return false

        return !/^[A-Za-z_][A-Za-z0-9_]*$/.test(uniqueTitle)
      },
      createUniqueTitleTaken () {

        const uniqueTitle = String(this.newAppUniqueTitle || ``).trim()

        if (!uniqueTitle) return false

        return this.apps.some((app) => app && app.uniqueTitle === uniqueTitle)
      },
      createUniqueTitleInvalid () {

        return this.createUniqueTitleEmpty || this.createUniqueTitleMalformed || this.createUniqueTitleTaken
      },
      copyTitleInvalid () {

        return this.copySubmitAttempted && !String(this.copyAppTitle || ``).trim()
      },
      copyUniqueTitleEmpty () {

        return this.copySubmitAttempted && !String(this.copyAppUniqueTitle || ``).trim()
      },
      copyUniqueTitleMalformed () {

        const uniqueTitle = String(this.copyAppUniqueTitle || ``).trim()

        if (!uniqueTitle) return false

        return !/^[A-Za-z_][A-Za-z0-9_]*$/.test(uniqueTitle)
      },
      copyUniqueTitleTaken () {

        const uniqueTitle = String(this.copyAppUniqueTitle || ``).trim()

        if (!uniqueTitle) return false

        return this.apps.some((app) => app && app.uniqueTitle === uniqueTitle)
      },
      copyUniqueTitleInvalid () {

        return this.copyUniqueTitleEmpty || this.copyUniqueTitleMalformed || this.copyUniqueTitleTaken
      },
      selectedTemplate () {

        return this.templates.find((item) => item.id === this.selectedTemplateId) || null
      },
      saveTemplateTitleInvalid () {

        return this.saveSubmitAttempted && !String(this.saveTemplateTitle || ``).trim()
      },
      saveTemplateIdInvalid () {

        const id = String(this.saveTemplateId || ``).trim()

        if (!id) return this.saveSubmitAttempted

        return !/^[A-Za-z_][A-Za-z0-9_]*$/.test(id)
      },
      saveTemplateInvalid () {

        return this.saveTemplateTitleInvalid || this.saveTemplateIdInvalid
      },
      importTitleInvalid () {

        return this.importSubmitAttempted && !String(this.importAppTitle || ``).trim()
      },
      importUniqueTitleEmpty () {

        return this.importSubmitAttempted && !String(this.importAppUniqueTitle || ``).trim()
      },
      importUniqueTitleMalformed () {

        const uniqueTitle = String(this.importAppUniqueTitle || ``).trim()

        if (!uniqueTitle) return false

        return !/^[A-Za-z_][A-Za-z0-9_]*$/.test(uniqueTitle)
      },
      importUniqueTitleTaken () {

        const uniqueTitle = String(this.importAppUniqueTitle || ``).trim()

        if (!uniqueTitle) return false

        return this.apps.some((app) => app && app.uniqueTitle === uniqueTitle)
      },
      importUniqueTitleInvalid () {

        return this.importUniqueTitleEmpty || this.importUniqueTitleMalformed || this.importUniqueTitleTaken
      }
    },
    watch: {

      newAppTitle (value) {

        if (!this.newAppUniqueTitleManuallyEdited) {

          this.newAppUniqueTitle = this.sanitizeUniqueTitle(value)
        }
      },
      deleteAppTitle () {

        this.deleteMismatch = false
      },
      copyAppTitle (value) {

        if (!this.copyAppUniqueTitleManuallyEdited) {

          this.copyAppUniqueTitle = this.sanitizeUniqueTitle(value)
        }
      },
      importAppTitle (value) {

        if (!this.importUniqueTitleManuallyEdited) {

          this.importAppUniqueTitle = this.sanitizeUniqueTitle(value)
        }
      }
    },
    beforeUnmount () {

      this.stopCopyPoll()

      if (this.closeTemplateMenu) {

        window.removeEventListener(`click`, this.closeTemplateMenu)
      }
    },
    mounted () {

      this.closeTemplateMenu = (event) => {

        const menu = this.$refs.templateMenu

        if (menu && !menu.contains(event.target)) {

          this.templateMenuOpen = false
        }
      }

      window.addEventListener(`click`, this.closeTemplateMenu)
      this.getApps().then(() => this.resumeCopy())
      this.loadAccountCapability()
    },
    methods: {

      async saveApp (event) {

        if (event) event.preventDefault()

        if (!this.app) return

        this.editError = ``

        const body = {
          title: this.editTitle,
          description: this.editDescription
        }

        try {

          await axios.put(`${VUE_APP_APP_SERVER_URL}/application/${this.uniqueTitle}`, body, {
            headers: {
              'Content-Type': 'application/json',
            },
            withCredentials: true
          })

          this.app.title = this.editTitle
          this.app.description = this.editDescription
          this.title = this.editTitle
          this.editAppModalVisible = false
        } catch (error) {

          this.editError = this.templateErrorMessage(error, `could not save the app`)
        }
      },

      formatDate (isoString) {

        if (!isoString) return ``

        const date = new Date(isoString)
        const now = new Date()
        const diffMs = now - date
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMs / 3600000)
        const diffDays = Math.floor(diffMs / 86400000)

        if (diffMins < 1) return `just now`
        if (diffMins < 60) return `${diffMins}m ago`
        if (diffHours < 24) return `${diffHours}h ago`
        if (diffDays < 7) return `${diffDays}d ago`

        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      },

      designerUrl () {

        if (!this.app) {

          return ``
        }

        return accountDesignerUrl(window.location.origin, this.app.uniqueTitle)
      },

      async loadApp (app) {

        if (!app) {

          this.app = null
          this.uniqueTitle = ``
          this.title = ``
          this.statsError = ``

          return
        }

        this.uniqueTitle = app ? app.uniqueTitle : ``
        this.title = app.title
        this.editTitle = app.title || ``
        this.editDescription = app.description || ``
        this.app = app
        this.stats = { spreadsheets: 0, records: 0, pages: 0, views: 0, lastUpdated: null }
        this.statsError = ``

        try {

          const response = await this.$http.get(`${VUE_APP_APP_SERVER_URL}/application/${app.uniqueTitle}/stats`, {withCredentials: true})
          this.stats = response.data
        } catch (error) {

          this.statsError = `could not load the counts for this app`
        }
      },
      sanitizeUniqueTitle (title) {

        return String(title || ``)
          .toLowerCase()
          .replace(/[^a-z0-9\s_]/g, ``)
          .replace(/\s+/g, `_`)
          .replace(/_+/g, `_`)
          .replace(/^_|_$/g, ``)
      },
      showCopyApp () {

        if (!this.copyInProgress) {

          this.copyAppTitle = `Copy of ${this.title}`
          this.copyAppUniqueTitleManuallyEdited = false
          this.copyAppUniqueTitle = this.sanitizeUniqueTitle(this.copyAppTitle)
          this.copyRecords = false
          this.copySubmitAttempted = false
          this.copyError = null
        }

        this.copyAppModalVisible = true
      },
      onCopyUniqueTitleInput (event) {

        const value = event && event.target ? event.target.value : event
        this.copyAppUniqueTitle = value
        this.copyAppUniqueTitleManuallyEdited = !!String(value || ``).trim()
      },
      stopCopyPoll () {

        if (this.copyPollTimer) {

          clearInterval(this.copyPollTimer)
          this.copyPollTimer = null
        }
      },
      async resumeCopy () {

        try {

          const response = await axios.get(`${VUE_APP_APP_SERVER_URL}/copy-jobs`, { withCredentials: true })
          const job = (response.data || [])[0]

          if (!job) return

          this.copyTargetUniqueTitle = job.payload && job.payload.uniqueTitle
          this.copyMessage = (job.payload && job.payload.message) || `waiting to start`
          this.copyProgress = job.progress || 0
          this.copyInProgress = true
          this.copyAppModalVisible = true
          this.pollCopy(job.id)
        } catch (error) {

          console.log(`error loading copy progress:`)
          console.log(error)
        }
      },
      pollCopy (jobId) {

        this.stopCopyPoll()

        this.copyPollTimer = setInterval(async () => {

          try {

            const response = await axios.get(`${VUE_APP_APP_SERVER_URL}/copy-jobs/${jobId}`, { withCredentials: true })
            const job = response.data
            this.copyProgress = job.progress || 0
            this.copyMessage = (job.payload && job.payload.message) || this.copyMessage

            if (job.status === `completed`) {

              this.stopCopyPoll()
              this.copyInProgress = false
              this.copyAppModalVisible = false
              this.copyBannerError = null
              await this.getApps(this.copyTargetUniqueTitle)
            }

            if (job.status === `failed`) {

              this.stopCopyPoll()
              this.copyInProgress = false
              this.copyError = job.errorMessage || `copy failed`
              this.copyBannerError = this.copyError
              this.copyAppModalVisible = true
            }
          } catch (error) {

            console.log(`error reading copy progress:`)
            console.log(error)
          }
        }, 400)
      },
      async startCopy (event) {

        event.preventDefault()

        if (this.copyInProgress) return

        this.copySubmitAttempted = true
        this.copyError = null

        if (this.copyTitleInvalid || this.copyUniqueTitleInvalid) return

        this.copyTargetUniqueTitle = String(this.copyAppUniqueTitle).trim()
        this.copyInProgress = true
        this.copyProgress = 0
        this.copyMessage = `waiting to start`
        this.copyBannerError = null

        try {

          const response = await axios.post(`${VUE_APP_APP_SERVER_URL}/application/${this.uniqueTitle}/copy`, {
            title: String(this.copyAppTitle).trim(),
            uniqueTitle: this.copyTargetUniqueTitle,
            copyRecords: this.copyRecords
          }, {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
          })

          this.copyMessage = response.data.message || this.copyMessage
          this.pollCopy(response.data.jobId)
        } catch (error) {

          this.copyInProgress = false
          const message = error.response && error.response.data
          this.copyError = typeof message === `string` && message ? message : `could not start the copy`
        }
      },
      async getApps (selectUniqueTitle) {

        let apps

        try {

          apps = await this.$http.get(`${VUE_APP_APP_SERVER_URL}/applications`, {withCredentials: true})
        } catch (error) {

          console.log(`error fetching apps:`)
          console.log(error)

          if (error.response && error.response.status === 401) {

            window.location.assign(VUE_APP_ACCOUNT_LOGIN_URL)
          }

          return
        }

        this.apps = apps.data

        this.appOptions = this.apps.map((app) => {

          if (!app) {

            return
          }

          return {
            value: app.uniqueTitle,
            text: app.title
          }
        })

        const selected = selectUniqueTitle
          ? this.apps.find((app) => app && app.uniqueTitle === selectUniqueTitle)
          : null

        this.loadApp(selected || this.apps[0])
      },
      showCreateApp () {

        this.createSubmitAttempted = false
        this.createError = ``
        this.createAppModalVisible = true
      },
      onNewUniqueTitleInput (event) {

        const value = event && event.target ? event.target.value : event

        this.newAppUniqueTitle = value
        this.newAppUniqueTitleManuallyEdited = !!String(value || ``).trim()
      },
      showEditApp () {

        this.editTitle = this.app.title || ``
        this.editDescription = this.app.description || ``
        this.editError = ``
        this.editAppModalVisible = true
      },
      showDeleteApp () {

        this.deleteAppTitle = ``
        this.deleteMismatch = false
        this.deleteError = ``
        this.deleteAppModalVisible = true
      },
      async createApp (event) {

        event.preventDefault()
        this.createSubmitAttempted = true
        this.createError = ``

        if (this.createTitleInvalid || this.createUniqueTitleInvalid) return

        const body = {
          title: String(this.newAppTitle).trim(),
          uniqueTitle: String(this.newAppUniqueTitle).trim()
        }

        try {

          await axios.post(`${VUE_APP_APP_SERVER_URL}/application/`, body, {
            headers: {
              'Content-Type': 'application/json',
            },
            withCredentials: true
          })
        } catch (error) {

          this.createError = this.templateErrorMessage(error, `could not create the app`)

          return
        }

        this.newAppTitle = ``
        this.newAppUniqueTitle = ``
        this.newAppUniqueTitleManuallyEdited = false
        this.createSubmitAttempted = false
        this.createAppModalVisible = false

        this.getApps(body.uniqueTitle)
      },
      async deleteApp (event) {

        event.preventDefault()
        this.deleteError = ``
        this.deleteMismatch = this.deleteAppTitle !== this.title

        if (this.deleteMismatch) return

        try {

          await axios.delete(`${VUE_APP_APP_SERVER_URL}/application/${this.uniqueTitle}`, {withCredentials: true})
        } catch (error) {

          this.deleteError = this.templateErrorMessage(error, `could not delete the app`)

          return
        }

        this.deleteAppTitle = ``
        this.deleteAppModalVisible = false

        this.getApps()
      },
      async loadAccountCapability () {

        try {

          const response = await axios.get(`${VUE_APP_APP_SERVER_URL}/account`, { withCredentials: true })

          this.canCreateTemplateApps = !!(response.data && response.data.canCreateTemplateApps)
        } catch (error) {

          this.canCreateTemplateApps = false
        }
      },
      templateErrorMessage (error, fallback) {

        const message = error && error.response && error.response.data

        return typeof message === `string` && message ? message : fallback
      },
      showImportTemplate () {

        this.templateMenuOpen = false
        this.templateError = ``
        this.importSubmitAttempted = false
        this.importTemplateModalVisible = true
        this.loadTemplates()
      },
      async loadTemplates () {

        this.templatesLoading = true

        try {

          const response = await axios.get(`${VUE_APP_APP_SERVER_URL}/template-apps`, { withCredentials: true })

          this.templates = response.data || []

          if (!this.templates.some((item) => item.id === this.selectedTemplateId)) {

            this.selectedTemplateId = this.templates[0] ? this.templates[0].id : ``
            this.selectTemplate(this.templates[0])
          }
        } catch (error) {

          this.templates = []
          this.selectedTemplateId = ``
          this.templateError = this.templateErrorMessage(error, `could not load template apps`)
        } finally {

          this.templatesLoading = false
        }
      },
      selectTemplate (template) {

        if (!template) {

          this.selectedTemplateId = ``
          this.importAppTitle = ``
          this.importAppUniqueTitle = ``
          return
        }

        this.selectedTemplateId = template.id
        this.importUniqueTitleManuallyEdited = false
        this.importAppTitle = template.title || ``
        this.importAppUniqueTitle = this.sanitizeUniqueTitle(template.title)
        this.importRecords = !!template.includeRecords
      },
      onImportUniqueTitleInput (event) {

        const value = event && event.target ? event.target.value : event

        this.importAppUniqueTitle = value
        this.importUniqueTitleManuallyEdited = !!String(value || ``).trim()
      },
      showSaveTemplate () {

        this.templateMenuOpen = false
        this.templateError = ``
        this.saveSubmitAttempted = false
        this.saveTemplateId = this.uniqueTitle
        this.saveTemplateTitle = this.title
        this.saveTemplateDescription = (this.app && this.app.description) || ``
        this.saveIncludeRecords = false
        this.saveTemplateModalVisible = true
      },
      async saveTemplate (event) {

        event.preventDefault()

        if (this.templateBusy) return

        this.saveSubmitAttempted = true
        this.templateError = ``

        if (this.saveTemplateInvalid) return

        this.templateBusy = true

        try {

          await axios.post(`${VUE_APP_APP_SERVER_URL}/template-apps`, {
            sourceUniqueTitle: this.uniqueTitle,
            templateId: String(this.saveTemplateId).trim(),
            title: String(this.saveTemplateTitle).trim(),
            description: String(this.saveTemplateDescription || ``),
            includeRecords: this.saveIncludeRecords
          }, {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
          })

          this.saveTemplateModalVisible = false
          this.templateNotice = `saved template ${String(this.saveTemplateTitle).trim()}`
        } catch (error) {

          this.templateError = this.templateErrorMessage(error, `could not save the template`)
        } finally {

          this.templateBusy = false
        }
      },
      async importTemplate (event) {

        event.preventDefault()

        if (this.templateBusy) return

        this.importSubmitAttempted = true
        this.templateError = ``

        if (!this.selectedTemplate || this.importTitleInvalid || this.importUniqueTitleInvalid) return

        this.templateBusy = true
        const uniqueTitle = String(this.importAppUniqueTitle).trim()

        try {

          await axios.post(`${VUE_APP_APP_SERVER_URL}/template-apps/${encodeURIComponent(this.selectedTemplateId)}/import`, {
            title: String(this.importAppTitle).trim(),
            uniqueTitle,
            copyRecords: this.selectedTemplate.includeRecords ? this.importRecords : false
          }, {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
          })

          this.importTemplateModalVisible = false
          this.templateNotice = `imported ${String(this.importAppTitle).trim()}`
          await this.getApps(uniqueTitle)
        } catch (error) {

          this.templateError = this.templateErrorMessage(error, `could not import the template`)
        } finally {

          this.templateBusy = false
        }
      },
      async deleteSelectedTemplate () {

        if (!this.selectedTemplate || this.templateBusy) return

        this.templateBusy = true
        this.templateError = ``
        const title = this.selectedTemplate.title

        try {

          await axios.delete(`${VUE_APP_APP_SERVER_URL}/template-apps/${encodeURIComponent(this.selectedTemplateId)}`, { withCredentials: true })
          this.templateNotice = `deleted template ${title}`
          await this.loadTemplates()
        } catch (error) {

          this.templateError = this.templateErrorMessage(error, `could not delete the template`)
        } finally {

          this.templateBusy = false
        }
      }
    },
    components: {
      ResourceNotice
    }
  }
</script>

<style scoped>

.apps-page {
  display: flex;
  flex-direction: column;
  background-color: #181916;
  overflow: hidden;
  min-height: 0;
}

/* ── Toolbar ── */
.apps-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background-color: #141412;
  border-bottom: 1px solid #2a2a27;
  flex-shrink: 0;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 5px;
  font-size: 0.78rem;
  font-weight: 500;
  color: #b0b0ae;
  background-color: transparent;
  border: 1px solid #333330;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  font-family: inherit;
}

.toolbar-btn:hover {
  background-color: #222220;
  color: #e8e8e6;
  border-color: #484845;
}

.toolbar-btn-icon {
  font-size: 0.7rem;
  opacity: 0.7;
}

.toolbar-btn-primary {
  color: #0f0f0d;
  background-color: #9ad12b;
  border-color: #9ad12b;
  font-weight: 600;
}

.toolbar-btn-primary:hover {
  color: #0f0f0d;
  background-color: #b8f046;
  border-color: #b8f046;
}

.toolbar-btn-primary .toolbar-btn-icon {
  opacity: 1;
  color: #0f0f0d;
}

/* ── Layout ── */
.apps-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sidebar-empty {
  margin: 4px 14px 0;
  color: #5a5a57;
  font-size: 0.8rem;
}

/* ── Sidebar ── */
.apps-sidebar {
  width: 260px;
  min-width: 260px;
  background-color: #141412;
  border-right: 1px solid #2a2a27;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.sidebar-header {
  padding: 14px 14px 8px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #5a5a57;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.app-list {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.app-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 14px;
  border: 0;
  border-left: 2px solid transparent;
  background: transparent;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
  color: #b0b0ae;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 500;
  text-align: left;
}

.app-item:hover {
  background-color: #1a1a17;
  color: #e8e8e6;
}

.app-item-active {
  background-color: #1a1a17;
  color: #e8e8e6;
  border-left-color: #9ad12b;
}

.app-item-icon {
  font-size: 0.75rem;
  color: #5a5a57;
  flex-shrink: 0;
}

.app-item-active .app-item-icon {
  color: #9ad12b;
}

.app-item-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.app-item-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-item-id {
  color: #5a5a57;
  font-size: 0.72rem;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Detail panel ── */
.apps-detail {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.detail-content {
  padding: 28px 32px;
}

.detail-header {
  margin-bottom: 20px;
}

.detail-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #e8e8e6;
  margin: 0 0 4px 0;
}

.detail-id {
  margin: 0;
  font-size: 0.78rem;
  color: #5a5a57;
}

.detail-id span {
  color: #b0b0ae;
}

.detail-description {
  font-size: 0.85rem;
  color: #b0b0ae;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 28px;
}

/* ── Stats grid ── */
.detail-stats-error {
  margin: 0 0 12px;
  color: #e05252;
  font-size: 0.78rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 28px;
}

.stat-card {
  background-color: #141412;
  border: 1px solid #2a2a27;
  border-radius: 6px;
  padding: 16px 14px;
  text-align: center;
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #e8e8e6;
  line-height: 1;
  margin-bottom: 8px;
}

.stat-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.72rem;
  font-weight: 500;
  color: #5a5a57;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.stat-icon {
  font-size: 0.65rem;
  color: #9ad12b;
}

.detail-last-updated {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #5a5a57;
  margin-bottom: 28px;
}

.last-updated-icon {
  font-size: 0.65rem;
  color: #3a3a37;
}

.detail-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border-radius: 5px;
  background: transparent;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 500;
  color: #b0b0ae;
  text-decoration: none;
  border: 1px solid #2a2a27;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}

.detail-action-btn:hover {
  background-color: #1e1e1b;
  color: #9ad12b;
  border-color: #9ad12b;
  text-decoration: none;
}

.detail-action-primary {
  color: #0f0f0d;
  background-color: #9ad12b;
  border-color: #9ad12b;
  font-weight: 600;
}

.detail-action-primary:hover {
  color: #0f0f0d;
  background-color: #b8f046;
  border-color: #b8f046;
}

.detail-action-danger {
  color: #f82471;
  border-color: #5a3038;
}

.detail-action-danger:hover {
  color: #f82471;
  background-color: #2a161c;
  border-color: #f82471;
}

.detail-action-danger .detail-action-icon {
  color: #f82471;
}

.detail-action-icon {
  font-size: 0.75rem;
  color: #9ad12b;
}

.detail-action-primary .detail-action-icon {
  color: #0f0f0d;
}

.detail-preview {
  border-left: 2px solid #2a2a27;
  padding-left: 20px;
  min-height: 100px;
}

.detail-empty,
.detail-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #4a4a47;
  font-size: 0.85rem;
  gap: 12px;
}

.detail-empty-icon {
  font-size: 1.6rem;
  color: #3a3a37;
}

.detail-empty-state {
  height: 100%;
  min-height: 300px;
}

.apps-page :deep(.resource-crash-banner) {
  margin: 12px 14px 0;
}

/* ── Modal styles ── */
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.modal-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #9ad12b;
  letter-spacing: 0.03em;
}

.modal-input {
  background-color: #141412;
  border: 1px solid #2a2a27;
  border-radius: 5px;
  padding: 10px 12px;
  font-size: 0.85rem;
  color: #e8e8e6;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
  width: 100%;
}

.modal-input::placeholder {
  color: #4a4a47;
}

.modal-input:focus {
  border-color: #9ad12b;
}

.modal-textarea {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

.modal-warning {
  color: #f82471;
  font-size: 0.9rem;
  margin: 0;
}

.modal-warning strong {
  color: #e8e8e6;
}

.modal-input-invalid {
  border-color: #f82471;
}

.modal-error {
  color: #f82471;
  font-size: 0.75rem;
  margin: 0;
}

.modal-hint {
  color: #5a5a57;
  font-size: 0.75rem;
  margin: 0;
  line-height: 1.4;
}

.modal-check {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e8e8e6;
  font-size: 0.85rem;
}

.copy-progress-banner,
.copy-progress {
  padding: 10px 14px;
  background-color: #141412;
  border-bottom: 1px solid #2a2a27;
}

.copy-progress {
  border-bottom: 0;
  padding: 4px 0 8px;
}

.copy-progress-label {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #b0b0ae;
  font-size: 0.78rem;
  margin-bottom: 8px;
}

.copy-progress-pct {
  margin-left: auto;
  color: #9ad12b;
}

.copy-dismiss {
  margin-left: auto;
  background: transparent;
  border: 0;
  color: #9ad12b;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.75rem;
}

.copy-progress-track {
  height: 6px;
  background-color: #2a2a27;
  border-radius: 99px;
  overflow: hidden;
}

.copy-progress-fill {
  height: 100%;
  background-color: #9ad12b;
  transition: width 0.2s ease;
}

</style>

<style>
/* Modal is teleported to body, so these rules cannot be scoped. */
.modal-content.app-modal {
  background-color: #181916;
  color: #e8e8e6;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.55);
}

.app-modal .modal-header {
  background-color: #141412;
  border-bottom: 1px solid #2a2a27;
  padding: 14px 16px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.app-modal .modal-title {
  color: #e8e8e6;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.3;
}

.app-modal .btn-close {
  filter: invert(1) grayscale(1);
  opacity: 0.65;
}

.app-modal .btn-close:hover {
  opacity: 1;
}

.app-modal .modal-body {
  background-color: #181916;
  padding: 18px 16px;
}

.app-modal .modal-footer {
  background-color: #141412;
  border-top: 1px solid #2a2a27;
  padding: 12px 16px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.app-modal .modal-footer .btn {
  border-radius: 5px;
  font-size: 0.78rem;
  font-weight: 500;
  padding: 6px 14px;
  box-shadow: none;
}

.app-modal .modal-footer .btn-secondary {
  color: #b0b0ae;
  background-color: transparent;
  border: 1px solid #333330;
}

.app-modal .modal-footer .btn-secondary:hover {
  color: #e8e8e6;
  background-color: #222220;
  border-color: #484845;
}

.app-modal .modal-footer .btn-primary {
  color: #141412;
  background-color: #9ad12b;
  border: 1px solid #9ad12b;
}

.app-modal .modal-footer .btn-primary:hover {
  color: #141412;
  background-color: #b0e04a;
  border-color: #b0e04a;
}

.toolbar-menu {
  position: relative;
}

.toolbar-menu-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  min-width: 190px;
  background: #1a1a17;
  border: 1px solid #333330;
  border-radius: 6px;
  padding: 4px;
}

.toolbar-menu-item {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 7px 10px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #d0d0ce;
  font: inherit;
  font-size: 0.78rem;
  text-align: left;
  cursor: pointer;
}

.toolbar-menu-item:hover {
  background: #262623;
  color: #e8e8e6;
}

.template-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 14px;
  background: #1c2414;
  color: #d7e8b0;
  border-bottom: 1px solid #2a2a27;
  font-size: 0.78rem;
}

.template-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow: auto;
}

.template-choice {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 5px;
  border: 1px solid #333330;
  background: transparent;
  color: #e8e8e6;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.template-choice-active {
  border-color: #9ad12b;
  background: #1c2414;
}

.template-choice-title {
  font-size: 0.82rem;
}

.template-choice-meta {
  font-size: 0.72rem;
  color: #8a8a86;
}

.template-delete {
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
  color: #f82471;
  font: inherit;
  font-size: 0.75rem;
  cursor: pointer;
}

@media (max-width: 900px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .apps-layout {
    flex-direction: column;
  }

  .apps-sidebar {
    width: 100%;
    min-width: 0;
    max-height: 220px;
    border-right: 0;
    border-bottom: 1px solid #2a2a27;
  }
}
</style>
